import { Host, Component, h, Prop, State, Listen, Element } from '@stencil/core';
import { getLanguagePath } from '../../../utils/router-utils';
import { getAPI } from '@sydle/ui-api';
import type { UserAPI } from '@sydle/ui-api';

@Component({
  tag: 'sy-site-blog-header-menu',
  styleUrl: 'sy-site-blog-header-menu.scss',
  shadow: true,
})
export class SySiteBlogHeaderMenu {
  @Element() el;

  /**
   * Items for the dropdown menu.
   * Should be an array of objects representing the menu items.
   */
  @Prop() dropdownItems: any;

  /**
   * Current page of the application.
   * Used to highlight the active menu item.
   */
  @Prop() currentPage: string;

  /**
   * Title of the menu. Defaults to 'Home' if not provided.
   */
  @Prop() homeLabel: string;

  @State() headlineLink = '/';
  @State() currentUrl = window.location.pathname;
  @State() baseUrl = window.location.origin;
  @State() opennedButtonId = '';

  @State() currentLanguage = 'en';

  @Listen('syDropdownWillClose')
  onDropdownWillClose(): void {
    this.opennedButtonId = '';
  }

  @Listen('syDropdownWillOpen')
  onDropdownWillOpen(event: any): void {
    const dropdownComponentId = event.composedPath()[0].id;
    this.opennedButtonId = dropdownComponentId.replace('dropdown-', '');
  }

  private observer: MutationObserver;

  componentWillLoad() {
    this.headlineLink = `${getLanguagePath()}/blog`;

    const userAPI = getAPI<UserAPI>('userAPI');
    this.currentLanguage = userAPI?.getCurrentLanguage() || 'en';

    // Monitorando mudanças na URL
    this.observer = new MutationObserver(() => {
      this.currentUrl = window.location.pathname;
    });
    this.observer.observe(document.head, { childList: true, subtree: true, attributes: true });
  }

  private closeDropdown(event) {
    const path = event.composedPath();

    const dropdownEl = path.find((p: EventTarget) => {
      return ['SY-DROPDOWN'].includes((p as Element).tagName);
    });

    dropdownEl?.close();
  }

  private closeTree(event) {
    const path = event.composedPath();

    const treeEl = path.find((p: EventTarget) => {
      return ['SY-TREE'].includes((p as Element).tagName);
    });

    treeEl?.collapseAll();
  }

  private renderSubItemsTree(subItems: any[]) {
    // console.log('currentUrl', this.currentUrl)
    return subItems.map((subItem) => (
      <a
        target="_self"
        href={`${this.headlineLink}${subItem.subItemURL}`}
        class={{ active: this.currentUrl === `/blog${subItem.subItemURL}` }}
        key={subItem._id}
        onClick={(event) => this.closeTree(event)}
      >
        <sy-text type="body">{subItem.subItemTitle}</sy-text>
      </a>
    ));
  }

  private renderSubItemsDropdown(subItems: any[]) {
    // console.log('currentUrl', this.currentUrl)
    return subItems.map((subItem) => (
      <a
        target="_self"
        href={`${this.headlineLink}${subItem.subItemURL}`}
        class={{ active: this.currentUrl === `${this.headlineLink}${subItem.subItemURL}` }}
        key={subItem._id}
        onClick={(event) => this.closeDropdown(event)}
      >
        <sy-text type="body">{subItem.subItemTitle}</sy-text>
      </a>
    ));
  }

  private renderMenuItem(el: any) {
    const hasSubItems = el.subItems?.length > 0;

    return (
      <div key={el._id} class="menu-item-container" data-sy-viewport>
        {hasSubItems ? (
          <button class="menu-item-button" aria-haspopup="true" aria-expanded="false" id={`item-${el._id}`}>
            <sy-text type="body">{el.itemTitle}</sy-text>
            <sy-icon
              name={this.opennedButtonId === el._id ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}
              aria-label="dropdown arrow"
            ></sy-icon>
          </button>
        ) : (
          <a
            target="_self"
            class={{ active: this.currentUrl === `${this.headlineLink}${el.redirectURL}`, 'menu-item': true }}
            href={`${this.baseUrl}${this.headlineLink}${el.redirectURL}`}
            id={`item-${el._id}`}
            onClick={(event) => this.closeDropdown(event)}
          >
            <sy-text type="body">{el.itemTitle}</sy-text>
          </a>
        )}

        {hasSubItems && (
          <sy-dropdown id={`dropdown-${el._id}`} toggle-by={`#item-${el._id}`} placement="bottom" offsetDistance={2}>
            <div class="dropdown-menu">{this.renderSubItemsDropdown(el.subItems)}</div>
          </sy-dropdown>
        )}
      </div>
    );
  }

  private renderTreeMenu() {
    return (
      <div class="tree-container">
        <div class="categories">
          {this.dropdownItems?.length ? (
            <sy-tree>
              {this.dropdownItems.map((el) => (
                <div key={el._id} class="menu-item-container">
                  {el.subItems?.length > 0 ? (
                    <sy-tree-node role="treeitem" type="inner-node" class="menu-item-container">
                      <sy-text class="menu-item-text">{el.itemTitle}</sy-text>
                      <div slot="child" class="subitems-container">
                        {this.renderSubItemsTree(el.subItems)}
                      </div>
                    </sy-tree-node>
                  ) : (
                    <a
                      target="_self"
                      class={{ active: this.currentUrl === `${this.headlineLink}${el.redirectURL}`, 'menu-item': true }}
                      href={`/${el.redirectURL}`}
                      id={`item-${el._id}`}
                      onClick={(event) => this.closeTree(event)}
                    >
                      <sy-text type="body">{el.itemTitle}</sy-text>
                    </a>
                  )}
                </div>
              ))}
            </sy-tree>
          ) : (
            <sy-blog-skeleton-load style={{ width: '16rem' }} />
          )}
        </div>
      </div>
    );
  }

  render() {
    return (
      <Host>
        <div class="menu">
          <div class="menu-content">
            <div>
              <a target="_self" href={this.headlineLink} class={{ active: this.currentUrl == `${this.headlineLink}/` }}>
                <sy-text type="body">{this.homeLabel}</sy-text>
              </a>
            </div>
            <div class="divider">
              <div class="vertical-bar"></div>
            </div>

            <div class="categories">
              {this.dropdownItems?.length ? (
                this.dropdownItems.map((el) => this.renderMenuItem(el))
              ) : (
                <sy-blog-skeleton-load style={{ width: '16rem' }} />
              )}
            </div>
          </div>
        </div>

        {this.renderTreeMenu()}
      </Host>
    );
  }
}
